import { useState, useMemo } from "react";
import { motion } from "motion/react";
import { Link } from "react-router-dom";
import { GitCompareArrows, Plus, Minus, Pencil, ArrowRight, Scissors } from "lucide-react";
import type { StrainGraph, GraphNode, GraphEdge } from "@/types";
import { computeDiff } from "@/lib/graph";
import { useStore } from "@/store/useStore";
import { Badge } from "@/components/Badge";
import { nodeMeta, edgeMeta } from "@/lib/vocabulary";
import { Button } from "@/components/Button";
import { cn } from "@/lib/cn";

export function DiffView({ graph }: { graph: StrainGraph }) {
  const allGraphs = useStore((s) => s.graphs);

  // candidate versions to diff against: the original of this fork, plus other forks
  const candidates = useMemo(() => {
    const list = allGraphs.filter((g) =>
      g.strain.id !== graph.strain.id &&
      (g.version.forked_from_strain_id === graph.version.forked_from_strain_id ||
       g.strain.id === graph.version.forked_from_strain_id ||
       g.version.forked_from_strain_id === graph.strain.id),
    );
    return list;
  }, [allGraphs, graph]);

  const [otherSlug, setOtherSlug] = useState(candidates[0]?.strain.slug ?? "");
  const other = allGraphs.find((g) => g.strain.slug === otherSlug) ?? candidates[0];

  // determine left/right order: original on left, fork on right
  const left = other && (!graph.version.fork_intent || (other.version.fork_intent === null))
    ? other : graph;
  const right = left === other ? graph : other;

  const diff = useMemo(() => {
    if (!other) return null;
    return computeDiff(left, right);
  }, [left, right, other]);

  if (!other || !diff) {
    return (
      <div className="h-full grid place-items-center bg-grid-fine px-6">
        <div className="text-center max-w-sm">
          <GitCompareArrows size={32} className="text-[var(--color-ink-faint)] mx-auto mb-3" />
          <h3 className="text-base font-semibold">No versions to compare</h3>
          <p className="text-[13px] text-[var(--color-ink-muted)] mt-1">
            Fork this strain to create a diffable version. Try it on the college strain — it has a steelman fork ready.
          </p>
          <Link to="/s/college-worth-it" className="inline-block mt-4">
            <Button variant="outline">Open college strain</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-grid-fine">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-1">diff view</div>
            <h2 className="text-lg font-semibold">What exactly changed?</h2>
          </div>
          {candidates.length > 1 && (
            <select
              value={otherSlug}
              onChange={(e) => setOtherSlug(e.target.value)}
              className="h-9 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] text-[13px] text-[var(--color-ink)]"
            >
              {candidates.map((c) => <option key={c.strain.id} value={c.strain.slug}>{c.strain.title}</option>)}
            </select>
          )}
        </div>

        {/* summary banner */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 mb-5"
        >
          <div className="flex items-center gap-2">
            <GitCompareArrows size={16} className="text-[var(--color-primary)]" />
            <span className="text-[14px] font-medium">{diff.summary}</span>
          </div>
          <div className="flex items-center gap-3 mt-2 text-[12px] text-[var(--color-ink-muted)]">
            <span className="font-mono">{left.strain.creator} → {right.strain.creator}</span>
          </div>
        </motion.div>

        {/* split view: nodes */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-2">
              {left.strain.creator} · original
            </div>
            <div className="space-y-1.5">
              {left.nodes.map((n) => {
                const added = diff.added_nodes.find((x) => x.id === n.id);
                const removed = diff.removed_nodes.find((x) => x.id === n.id);
                const edited = diff.edited_nodes.find((x) => x.left.id === n.id);
                return (
                  <DiffNodeLine
                    key={n.id}
                    node={n}
                    status={removed ? "removed" : edited ? "edited" : "unchanged"}
                  />
                );
              })}
            </div>
          </div>

          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-2">
              {right.strain.creator} · fork
            </div>
            <div className="space-y-1.5">
              {right.nodes.map((n) => {
                const added = diff.added_nodes.find((x) => x.id === n.id);
                const edited = diff.edited_nodes.find((x) => x.right.id === n.id);
                return (
                  <DiffNodeLine
                    key={n.id}
                    node={n}
                    status={added ? "added" : edited ? "edited" : "unchanged"}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* change lists */}
        <div className="space-y-4">
          {diff.edited_nodes.length > 0 && (
            <DiffSection title="Edited nodes" icon={<Pencil size={13} />} tone="amber">
              {diff.edited_nodes.map(({ left, right, field }) => (
                <div key={left.id} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
                  <Badge tone="muted" mono>{field}</Badge>
                  <div className="mt-2 text-[13px] text-[var(--color-ink-muted)] line-through">{left.text}</div>
                  <div className="mt-1 text-[13px] text-[var(--color-ink)]">{right.text}</div>
                </div>
              ))}
            </DiffSection>
          )}

          {diff.added_nodes.length > 0 && (
            <DiffSection title={`Added nodes (${diff.added_nodes.length})`} icon={<Plus size={13} />} tone="ok">
              {diff.added_nodes.map((n) => (
                <div key={n.id} className="rounded-lg border border-[rgba(52,211,153,0.25)] bg-[rgba(52,211,153,0.05)] p-3">
                  <DiffNodeLine node={n} status="added" />
                </div>
              ))}
            </DiffSection>
          )}

          {diff.removed_nodes.length > 0 && (
            <DiffSection title={`Removed nodes (${diff.removed_nodes.length})`} icon={<Minus size={13} />} tone="danger">
              {diff.removed_nodes.map((n) => (
                <div key={n.id} className="rounded-lg border border-[rgba(244,63,94,0.2)] bg-[rgba(244,63,94,0.04)] p-3">
                  <DiffNodeLine node={n} status="removed" />
                </div>
              ))}
            </DiffSection>
          )}

          {diff.type_changed_edges.length > 0 && (
            <DiffSection title={`Changed relations (${diff.type_changed_edges.length})`} icon={<ArrowRight size={13} />} tone="info">
              {diff.type_changed_edges.map(({ left, right }) => (
                <div key={left.id} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3 flex items-center gap-2">
                  <Badge tone="muted" mono>{edgeMeta(left.relation).label}</Badge>
                  <ArrowRight size={12} className="text-[var(--color-ink-faint)]" />
                  <Badge tone="primary" mono>{edgeMeta(right.relation).label}</Badge>
                </div>
              ))}
            </DiffSection>
          )}

          {diff.added_edges.length > 0 && (
            <DiffSection title={`Added edges (${diff.added_edges.length})`} icon={<Plus size={13} />} tone="ok">
              {diff.added_edges.map((e) => <DiffEdgeLine key={e.id} edge={e} status="added" graph={right} />)}
            </DiffSection>
          )}

          {diff.removed_edges.length > 0 && (
            <DiffSection title={`Removed edges (${diff.removed_edges.length})`} icon={<Minus size={13} />} tone="danger">
              {diff.removed_edges.map((e) => <DiffEdgeLine key={e.id} edge={e} status="removed" graph={left} />)}
            </DiffSection>
          )}

          {diff.added_nodes.length === 0 && diff.removed_nodes.length === 0 && diff.edited_nodes.length === 0 &&
            diff.added_edges.length === 0 && diff.removed_edges.length === 0 && (
              <div className="text-center py-8 text-[var(--color-ink-faint)] text-[13px]">
                No structural changes between these versions.
              </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DiffNodeLine({ node, status }: { node: GraphNode; status: "added" | "removed" | "edited" | "unchanged" }) {
  const meta = nodeMeta(node.type);
  const Icon = meta.icon;
  const color =
    status === "added" ? "border-l-[var(--color-support)]"
    : status === "removed" ? "border-l-[var(--color-rebut)] opacity-50"
    : status === "edited" ? "border-l-[var(--color-secondary)]"
    : "border-l-[var(--color-border)]";
  return (
    <div className={cn("pl-2 border-l-2 py-1", color)}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon size={10} style={{ color: meta.color }} />
        <span className="text-[9px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)]">{meta.short}</span>
      </div>
      <div className={cn("text-[12px] leading-snug", status === "removed" ? "text-[var(--color-ink-muted)] line-through" : "text-[var(--color-ink-soft)]")}>
        {node.text}
      </div>
    </div>
  );
}

function DiffEdgeLine({ edge, status, graph }: { edge: GraphEdge; status: "added" | "removed"; graph: StrainGraph }) {
  const meta = edgeMeta(edge.relation);
  const Icon = meta.icon;
  const sn = graph.nodes.find((n) => n.id === edge.source);
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-2.5 flex items-center gap-2">
      <Icon size={12} style={{ color: meta.color }} />
      <span className="text-[9px] font-mono uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
      <span className="text-[12px] text-[var(--color-ink-soft)] truncate">{sn?.text.slice(0, 40)}…</span>
    </div>
  );
}

function DiffSection({ title, icon, tone, children }: { title: string; icon: React.ReactNode; tone: string; children: React.ReactNode }) {
  return (
    <div>
      <div className={cn("flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider mb-2",
        tone === "ok" ? "text-[var(--color-support)]"
        : tone === "danger" ? "text-[var(--color-rebut)]"
        : tone === "amber" ? "text-[var(--color-secondary)]"
        : "text-[var(--color-define)]")}>
        {icon} {title}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
